import React, { useEffect, useMemo, useRef } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import useAccessOverrides from '../../hooks/useAccessOverrides';
import { allowedPathsForRole, managedPaths } from '../../config/accessControl';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import Header from './Header';
import QuickActionButton from '../QuickActionButton';
import RouteErrorBoundary from '../RouteErrorBoundary';
import PushNotificationBanner from '../PushNotificationBanner';
import { Loader2 } from 'lucide-react';

// Find the longest path in `set` that prefix-matches `pathname` (either an
// exact match or `pathname.startsWith(p + '/')`).
function _longestPrefix(pathname, set) {
  let best = null;
  for (const p of set) {
    if (!p || p === '/') continue;
    if (pathname === p || pathname.startsWith(p + '/')) {
      if (!best || p.length > best.length) best = p;
    }
  }
  return best;
}

// A pathname is "governed" if a managed nav-path prefix-matches it. The
// allowed-set tracks every path the user *can* reach (nav items they're
// granted + UNIVERSAL_ALLOWED_PATHS). The rule is: if a more-specific
// (or equally-specific) allowed prefix covers the pathname, allow. Else,
// if any managed prefix matches but isn't in the allowed-set, deny. If no
// managed prefix matches, the page is free.
//
// Why two-prefix matching (instead of just `allowedSet.has(govern)`)? Because
// allowed-paths like `/admin/staff` come from UNIVERSAL_ALLOWED_PATHS and are
// NOT in managedPaths (no sidebar item points to them). Without this, the
// shorter `/admin` (in managedPaths) wins and blocks roles that don't have
// `/admin` access — even though `/admin/staff` was meant to be universally
// reachable.
function pathIsAllowed(pathname, allowedSet, managed) {
  const longestAllowed = _longestPrefix(pathname, allowedSet);
  const longestManaged = _longestPrefix(pathname, managed);
  // Allowed-set has equal-or-more-specific coverage → allow.
  if (longestAllowed && (!longestManaged || longestAllowed.length >= longestManaged.length)) {
    return true;
  }
  // Managed prefix governs this pathname and no allowed prefix covers it.
  if (longestManaged) return false;
  // No nav surface matches → free path.
  return true;
}

export default function Layout({ title = 'Dashboard' }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const overrides = useAccessOverrides();
  const lastDeniedPath = useRef(null);

  const allowed = useMemo(
    () => (user?.role
      ? allowedPathsForRole(user.role, overrides, { isFranchiseStore: !!user?.is_franchise_store })
      : new Set()),
    [user?.role, user?.is_franchise_store, overrides]
  );
  const managed = useMemo(() => managedPaths(), []);

  useEffect(() => {
    if (!user?.role) return;
    // CRITICAL: wait for the per-user overrides to load before evaluating
    // the guard. Otherwise we can race-redirect a legitimately granted user
    // before their grant has been fetched from /me/overrides.
    if (!overrides?._loaded) return;
    if (pathIsAllowed(location.pathname, allowed, managed)) return;
    if (lastDeniedPath.current !== location.pathname) {
      lastDeniedPath.current = location.pathname;
      toast.error('You do not have access to that page.');
    }
    navigate('/dashboard', { replace: true });
  }, [location.pathname, allowed, managed, user?.role, overrides?._loaded, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#DC2626] mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-container bg-slate-50" data-app-shell>
      <Sidebar />
      <div className="main-content">
        <Header title={title} />
        <main className="p-4 lg:p-5 lg:pr-20 page-transition w-full box-border overflow-x-hidden">
          <div className="max-w-full">
            <RouteErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>
      </div>
      <MobileNav />
      <QuickActionButton />
      <PushNotificationBanner />
    </div>
  );
}
